import React from 'react';
import { Github, FileCode, ArrowRight } from 'lucide-react';
import { REPO_URL, SELF_HOST_QUICKSTART_URL } from '../constants/links';

interface SelfHostNoticeProps {
  /** Why the Take Survey tab is gated. From `/api/v1/survey/config`.
   *  `undefined` means the backend didn't report a reason (older server, or
   *  the request failed) — fall back to the generic hosted-demo messaging. */
  reason?: 'missing_extra' | 'missing_api_key' | null;
}

const SelfHostNotice: React.FC<SelfHostNoticeProps> = ({ reason }) => {
  const isMissingExtra = reason === 'missing_extra';
  const isMissingKey = reason === 'missing_api_key';

  const headline = isMissingKey
    ? "Take Survey — set an LLM API key to enable"
    : "Take Survey — available via self-hosting";

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border p-8">
        <div className="flex items-center mb-4 space-x-3">
          <FileCode className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold">{headline}</h2>
        </div>

        {isMissingKey ? (
          <>
            <p className="text-sm text-gray-700 mb-3">
              This backend has the <code className="px-1 rounded bg-gray-100">[live]</code>{' '}
              extra installed but no LLM API key in the environment. Set
              one in the deployment's <code className="px-1 rounded bg-gray-100">.env</code>{' '}
              or process environment and restart:
            </p>
            <pre className="text-xs bg-gray-50 border rounded p-3 mb-6 overflow-x-auto">
{`export OPENAI_API_KEY=sk-...   # or
export GOOGLE_API_KEY=...        # for Gemini models
surveyshield serve`}
            </pre>
          </>
        ) : isMissingExtra ? (
          <>
            <p className="text-sm text-gray-700 mb-3">
              The <code className="px-1 rounded bg-gray-100">[live]</code>{' '}
              optional extra isn't installed on this backend, so the live
              runtime can't start a browser. Install browser-use + Playwright
              and restart:
            </p>
            <pre className="text-xs bg-gray-50 border rounded p-3 mb-6 overflow-x-auto">
{`pip install 'surveyshield-py[live]'
playwright install chromium

export OPENAI_API_KEY=sk-...   # or GOOGLE_API_KEY
surveyshield serve`}
            </pre>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700 mb-3">
              The live runtime drives a real browser through your survey
              end-to-end. Each run takes 5–10 minutes, spawns a Chromium
              process, and burns real LLM credits. The hosted Survey Shield
              demo doesn't expose this path because we don't subsidize
              per-run costs and won't ask anyone to paste an API key into a
              web form.
            </p>
            <p className="text-sm text-gray-700 mb-3">
              For instrument-design feedback, the{' '}
              <strong>Instrument Review</strong> tab is the recommended path
              — it's free, takes ~30 seconds, and runs entirely server-side
              without any setup on your end.
            </p>
            <p className="text-sm text-gray-700 mb-6">
              To use the live runtime, install the package with the{' '}
              <code className="px-1 rounded bg-gray-100">[live]</code>{' '}
              extra and set your own API key:
            </p>
            <pre className="text-xs bg-gray-50 border rounded p-3 mb-6 overflow-x-auto">
{`pip install 'surveyshield-py[live]'
playwright install chromium

export OPENAI_API_KEY=sk-...   # or GOOGLE_API_KEY
surveyshield serve

# visit http://localhost:8000 — Take Survey now shows the form`}
            </pre>
          </>
        )}

        <p className="text-xs text-gray-700 mb-4">
          Or call <code className="px-1 rounded bg-gray-100">surveyshield.take_survey(...)</code>{' '}
          directly from a Python script if you'd rather skip the web UI.
        </p>

        <div className="flex flex-wrap gap-3">
          <a
            href={REPO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800"
          >
            <Github className="w-4 h-4" />
            <span>View on GitHub</span>
          </a>
          <a
            href={SELF_HOST_QUICKSTART_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            <span>Self-host quickstart</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default SelfHostNotice;
